/**
 * ControlBar - Bottom control bar for text reader and navigation
 */
export default class ControlBar {
    constructor(widget) {
        this.widget = widget;
        this.container = null;
        this.isVisible = false;
        this.readingProgress = 0;

        this._create();
        this._bindEvents();
    }

    /**
     * Create control bar DOM
     */
    _create() {
        this.container = document.createElement('div');
        this.container.className = 'accessibility-control-bar';
        this.container.setAttribute('role', 'toolbar');
        this.container.setAttribute('aria-label', 'Accessibility Controls');
        this.container.style.display = 'none';

        const config = this.widget.config.controlBar || {};
        const buttons = config.buttons || {};

        // Volume
        if (buttons.volume !== false) {
            this.container.appendChild(this._createButton('volume', 'control.volume', 'volume', `
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>
            `));
        }

        this.container.appendChild(this._createSeparator());

        // Previous
        if (buttons.previous !== false) {
            this.container.appendChild(this._createButton('previous', 'control.previous', 'previous', `
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/></svg>
            `));
        }

        // Play/Pause
        if (buttons.playPause !== false) {
            this.container.appendChild(this._createButton('playPause', 'control.play_pause', 'playPause', `
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            `));
        }

        // Next
        if (buttons.next !== false) {
            this.container.appendChild(this._createButton('next', 'control.next', 'next', `
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/></svg>
            `));
        }

        this.container.appendChild(this._createSeparator());

        // Progress bar
        if (buttons.progress !== false) {
            this.container.appendChild(this._createProgressBar());
        }

        this.container.appendChild(this._createSeparator());

        // Settings
        if (buttons.settings !== false) {
            this.container.appendChild(this._createButton('settings', 'control.settings', 'settings', `
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg>
            `));
        }

        // Hide
        if (buttons.hide !== false) {
            this.container.appendChild(this._createButton('hide', 'control.hide', 'hideBar', `
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            `));
        }

        document.body.appendChild(this.container);
    }

    /**
     * Create a control bar button
     */
    _createButton(id, labelKey, action, iconHtml) {
        const btn = document.createElement('button');
        btn.className = 'control-bar-btn';
        btn.dataset.action = action;
        btn.setAttribute('type', 'button');
        btn.setAttribute('aria-label', this.widget.i18n.t(labelKey));
        btn.setAttribute('title', this.widget.i18n.t(labelKey));
        btn.innerHTML = iconHtml;
        return btn;
    }

    /**
     * Create separator
     */
    _createSeparator() {
        const sep = document.createElement('div');
        sep.className = 'control-bar-separator';
        return sep;
    }

    /**
     * Create progress bar
     */
    _createProgressBar() {
        const wrapper = document.createElement('div');
        wrapper.className = 'control-bar-progress';
        wrapper.setAttribute('role', 'progressbar');
        wrapper.setAttribute('aria-valuemin', '0');
        wrapper.setAttribute('aria-valuemax', '100');
        wrapper.setAttribute('aria-valuenow', '0');
        wrapper.setAttribute('aria-label', this.widget.i18n.t('control.progress'));

        const fill = document.createElement('div');
        fill.className = 'progress-fill';
        wrapper.appendChild(fill);

        const text = document.createElement('span');
        text.className = 'progress-text';
        text.textContent = '0%';
        wrapper.appendChild(text);

        // Click to jump
        wrapper.addEventListener('click', (e) => {
            const rect = wrapper.getBoundingClientRect();
            const percent = (e.clientX - rect.left) / rect.width;
            const scrollPos = percent * (document.documentElement.scrollHeight - window.innerHeight);
            window.scrollTo({ top: scrollPos, behavior: 'smooth' });
        });

        return wrapper;
    }

    /**
     * Bind events
     */
    _bindEvents() {
        // Button clicks
        this.container.addEventListener('click', (e) => {
            const btn = e.target.closest('.control-bar-btn');
            if (!btn) return;
            this._handleAction(btn.dataset.action);
        });

        // Scroll progress tracking
        window.addEventListener('scroll', () => {
            if (this.isVisible) this._updateProgress();
        });

        // Show control bar when text reader is active
        this.widget.events.on('feature:toggle', (data) => {
            if (data.key === 'textReader') {
                if (data.active) {
                    this.show();
                    const textReader = this.widget.features.textReader;
                    if (textReader) {
                        requestAnimationFrame(() => this._updatePlayButton(textReader.isPlaying));
                    }
                } else {
                    this.hide();
                }
            }
        });

        // Keyboard shortcuts
        document.addEventListener('keydown', (e) => {
            if (!this.isVisible) return;
            if (this._isInputFocused()) return;

            if (e.ctrlKey && e.code === 'Space') {
                e.preventDefault();
                this._handleAction('playPause');
            }
            if (e.ctrlKey && e.code === 'ArrowLeft') {
                e.preventDefault();
                this._handleAction('previous');
            }
            if (e.ctrlKey && e.code === 'ArrowRight') {
                e.preventDefault();
                this._handleAction('next');
            }
        });
    }

    /**
     * Handle button actions
     */
    _handleAction(action) {
        const textReader = this.widget.features.textReader;

        switch (action) {
            case 'playPause':
                if (textReader && textReader.togglePlay) {
                    textReader.togglePlay();
                    this._updatePlayButton(textReader.isPlaying);
                }
                break;
            case 'previous':
                if (textReader && textReader.previousSection) {
                    textReader.previousSection();
                    this._updatePlayButton(textReader.isPlaying);
                }
                break;
            case 'next':
                if (textReader && textReader.nextSection) {
                    textReader.nextSection();
                    this._updatePlayButton(textReader.isPlaying);
                }
                break;
            case 'volume':
                this._toggleVolumePopup();
                break;
            case 'settings':
                this._toggleSpeedPopup();
                break;
            case 'hideBar':
                this.hide();
                break;
        }
    }

    /**
     * Update play/pause button icon
     */
    _updatePlayButton(isPlaying) {
        const btn = this.container.querySelector('[data-action="playPause"]');
        if (!btn) return;
        if (isPlaying) {
            btn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>';
            btn.classList.add('active');
        } else {
            btn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
            btn.classList.remove('active');
        }
    }

    /**
     * Toggle volume popup with slider
     */
    _toggleVolumePopup() {
        let popup = this.container.querySelector('.control-bar-volume-popup');
        if (popup) {
            popup.remove();
            return;
        }

        const textReader = this.widget.features.textReader;
        if (!textReader) return;

        popup = document.createElement('div');
        popup.className = 'control-bar-volume-popup';

        const label = document.createElement('span');
        label.className = 'volume-label';
        label.textContent = Math.round(textReader.defaultVolume * 100) + '%';

        const slider = document.createElement('input');
        slider.type = 'range';
        slider.className = 'volume-slider';
        slider.min = '0';
        slider.max = '100';
        slider.value = String(Math.round(textReader.defaultVolume * 100));
        slider.setAttribute('aria-label', 'Volume');

        slider.addEventListener('input', () => {
            const vol = parseInt(slider.value, 10) / 100;
            textReader.setVolume(vol);
            label.textContent = slider.value + '%';
            this._updateVolumeIcon(vol);
        });

        popup.appendChild(slider);
        popup.appendChild(label);

        const volumeBtn = this.container.querySelector('[data-action="volume"]');
        if (volumeBtn) {
            volumeBtn.style.position = 'relative';
            volumeBtn.appendChild(popup);
        }

        // Close popup on outside click
        const closeHandler = (e) => {
            if (!popup.contains(e.target) && e.target !== volumeBtn && !volumeBtn.contains(e.target)) {
                popup.remove();
                document.removeEventListener('click', closeHandler);
            }
        };
        setTimeout(() => document.addEventListener('click', closeHandler), 0);
    }

    /**
     * Toggle speed control popup
     */
    _toggleSpeedPopup() {
        let popup = this.container.querySelector('.control-bar-speed-popup');
        if (popup) {
            popup.remove();
            return;
        }

        const textReader = this.widget.features.textReader;
        if (!textReader) return;

        popup = document.createElement('div');
        popup.className = 'control-bar-speed-popup';

        const speeds = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];
        speeds.forEach(speed => {
            const btn = document.createElement('button');
            btn.className = 'speed-option';
            btn.setAttribute('type', 'button');
            btn.textContent = speed + 'x';
            if (Math.abs(textReader.defaultSpeed - speed) < 0.01) {
                btn.classList.add('active');
            }
            btn.addEventListener('click', () => {
                textReader.setSpeed(speed);
                popup.querySelectorAll('.speed-option').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                // Update settings button label
                const settingsBtn = this.container.querySelector('[data-action="settings"]');
                if (settingsBtn) settingsBtn.setAttribute('title', speed + 'x');
            });
            popup.appendChild(btn);
        });

        const settingsBtn = this.container.querySelector('[data-action="settings"]');
        if (settingsBtn) {
            settingsBtn.style.position = 'relative';
            settingsBtn.appendChild(popup);
        }

        // Close on outside click
        const closeHandler = (e) => {
            if (!popup.contains(e.target) && e.target !== settingsBtn && !settingsBtn.contains(e.target)) {
                popup.remove();
                document.removeEventListener('click', closeHandler);
            }
        };
        setTimeout(() => document.addEventListener('click', closeHandler), 0);
    }

    /**
     * Update volume icon based on level
     */
    _updateVolumeIcon(volume) {
        const btn = this.container.querySelector('[data-action="volume"]');
        if (!btn) return;
        if (volume === 0) {
            btn.querySelector('svg').innerHTML = '<path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>';
        } else if (volume < 0.5) {
            btn.querySelector('svg').innerHTML = '<path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>';
        } else {
            btn.querySelector('svg').innerHTML = '<path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>';
        }
    }

    /**
     * Update reading progress
     */
    _updateProgress() {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const progress = docHeight > 0 ? Math.min(100, Math.max(0, (scrollTop / docHeight) * 100)) : 0;

        this.readingProgress = progress;

        const fill = this.container.querySelector('.progress-fill');
        const text = this.container.querySelector('.progress-text');
        const wrapper = this.container.querySelector('.control-bar-progress');

        if (fill) fill.style.width = progress + '%';
        if (text) text.textContent = Math.round(progress) + '%';
        if (wrapper) wrapper.setAttribute('aria-valuenow', String(Math.round(progress)));
    }

    /**
     * Check if an input element is focused
     */
    _isInputFocused() {
        const el = document.activeElement;
        return el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    }

    /**
     * Show control bar
     */
    show() {
        this.container.style.display = 'flex';
        this.isVisible = true;
        this._updateProgress();
        this.widget.events.emit('controlbar:show');
    }

    /**
     * Hide control bar
     */
    hide() {
        this.container.style.display = 'none';
        this.isVisible = false;
        this.widget.events.emit('controlbar:hide');
    }

    /**
     * Destroy control bar
     */
    destroy() {
        if (this.container) {
            this.container.remove();
        }
    }
}
